import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { z } from 'zod'
import { applyVocabulary, DEFAULT_VOCABULARY, type Vocabulary } from './vocabulary'

/**
 * Internationalisation.
 *
 * - Keys are the English source text (`t('Admit student')`), so English needs no dictionary and a
 *   missing French entry degrades to readable English rather than a raw key.
 * - French strings live in one JSON file per app area (`locales/fr/<namespace>.json`), which keeps
 *   parallel work conflict-free. English files are only needed for plural forms.
 * - Interpolation uses {{name}}; plurals use i18next suffixes (`"{{count}} students_one"`).
 */
export const LANGUAGES = [
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'en', label: 'English', short: 'EN' },
] as const
export type Language = (typeof LANGUAGES)[number]['code']
export const DEFAULT_LANGUAGE: Language = 'fr'

export const NAMESPACES = [
  'common',
  'auth',
  'account',
  'dashboard',
  'students',
  'staff',
  'academic',
  'settings',
  'portal',
  'attendance',
  'announcements',
  'finance',
  'exams',
  'leave',
  'library',
  'imports',
  'timetable',
  'platform',
  'setup',
  'lms',
  'site',
  'messaging',
  'courses',
  'admissions',
  'homepage',
  'promotion',
] as const
export type Namespace = (typeof NAMESPACES)[number]

type Dictionary = Record<string, string>
const files = import.meta.glob<{ default: Dictionary }>('./locales/*/*.json', { eager: true })

const resources: Record<string, Record<string, Dictionary>> = { fr: {}, en: {} }
for (const [path, mod] of Object.entries(files)) {
  const match = /locales\/(\w+)\/(\w+)\.json$/.exec(path)
  if (!match) continue
  const [, lng, ns] = match
  if (lng && ns) (resources[lng] ??= {})[ns] = mod.default
}

// The texts as written (default vocabulary), kept to rebuild the dictionaries for each school.
const pristine: Record<string, Record<string, Dictionary>> = JSON.parse(JSON.stringify(resources)) as Record<
  string,
  Record<string, Dictionary>
>
let currentVocabulary: Vocabulary = DEFAULT_VOCABULARY

/**
 * Speaks a school's vocabulary (étudiant, session, niveau, groupe…): rewrites the French
 * dictionaries, and builds English ones from their keys (the English source texts). Templates are
 * rewritten before interpolation, so names inserted into texts are never changed.
 */
export function setVocabulary(v: Vocabulary, notify = true): boolean {
  if (JSON.stringify(v) === JSON.stringify(currentVocabulary)) return false
  currentVocabulary = v
  for (const ns of NAMESPACES) {
    const fr = pristine.fr?.[ns] ?? {}
    const en = pristine.en?.[ns] ?? {}
    const frOut: Dictionary = {}
    const enOut: Dictionary = {}
    for (const [key, value] of Object.entries(fr)) {
      frOut[key] = applyVocabulary(value, 'fr', v)
      const english = applyVocabulary(key, 'en', v)
      if (english !== key) enOut[key] = english
    }
    for (const [key, value] of Object.entries(en)) enOut[key] = applyVocabulary(value, 'en', v)
    i18n.removeResourceBundle('fr', ns)
    i18n.removeResourceBundle('en', ns)
    i18n.addResourceBundle('fr', ns, frOut)
    i18n.addResourceBundle('en', ns, enOut)
  }
  pendingNotification = true
  if (notify) flushVocabularyChange()
  return true
}
let pendingNotification = false

/** Re-renders the translated components (they listen to "languageChanged") after a change. */
export function flushVocabularyChange(): void {
  if (!pendingNotification) return
  pendingNotification = false
  i18n.emit('languageChanged', i18n.language)
}

export function isLanguage(value: unknown): value is Language {
  return LANGUAGES.some((l) => l.code === value)
}

void i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LANGUAGE,
  fallbackLng: 'en',
  ns: [...NAMESPACES],
  defaultNS: 'common',
  fallbackNS: 'common',
  keySeparator: false,
  nsSeparator: false,
  interpolation: { escapeValue: false },
  returnNull: false,
  returnEmptyString: false,
  react: { useSuspense: false },
})

// Zod's built-in messages (e.g. "Too big", used where a schema has no custom text) follow the UI language.
function syncZodLocale(lng: string): void {
  z.config(lng === 'en' ? z.locales.en() : z.locales.fr())
}
syncZodLocale(i18n.language)
i18n.on('languageChanged', syncZodLocale)

/** Translate a string that may belong to any namespace (enum labels, validation messages, nav). */
export function tAny(key: string, options?: Record<string, unknown>): string {
  return i18n.t(key, { ns: [...NAMESPACES], ...options })
}

/** BCP-47 locale used by Intl formatters for the active language. */
export function intlLocale(): string {
  return i18n.language === 'en' ? 'en-US' : 'fr-FR'
}

export default i18n
